import { useEffect, useState } from "react";
import { BlockNoteEditor, type PartialBlock } from "@blocknote/core";
import "@blocknote/core/fonts/inter.css";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { useCreateBlockNote } from "@blocknote/react";
import type { BodyFormat } from "@tih/shared";
import { ApiError, uploadFileWithAuth } from "../../api/client";

const DEFAULT_BLOCKS: PartialBlock[] = [
  {
    type: "paragraph",
    content: "Write something heaven-worthy.",
  },
];

/** Serialized default BlockNote document for new posts/pages. */
export const DEFAULT_EDITOR_BODY = JSON.stringify(DEFAULT_BLOCKS);

type Props = {
  body: string;
  bodyFormat: BodyFormat;
  disabled?: boolean;
  onChange: (body: string) => void;
};

type EditorProps = {
  initialBlocks: PartialBlock[];
  loadError: string | null;
  disabled: boolean;
  onChange: (body: string) => void;
};

async function resolveInitialBlocks(body: string, bodyFormat: BodyFormat): Promise<PartialBlock[]> {
  if (bodyFormat === "BLOCKS") {
    try {
      const parsed = JSON.parse(body) as PartialBlock[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch {
      /* fall through to default */
    }
    return DEFAULT_BLOCKS;
  }

  const trimmed = body.trim();
  if (!trimmed) return DEFAULT_BLOCKS;

  const temp = BlockNoteEditor.create();
  return temp.tryParseMarkdownToBlocks(trimmed);
}

async function uploadFile(file: File): Promise<string> {
  const data = await uploadFileWithAuth(file, { formIdentifier: "admin-blog-inline" });
  return data.uploadsUrl;
}

/** Accept a bare video id or common YouTube URL shapes. */
function parseYoutubeVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed;
  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "youtu.be") {
      const id = u.pathname.split("/").filter(Boolean)[0] ?? "";
      return /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
      const v = u.searchParams.get("v");
      if (v && /^[\w-]{11}$/.test(v)) return v;
      const parts = u.pathname.split("/").filter(Boolean);
      const embedIdx = parts.indexOf("embed");
      if (embedIdx >= 0 && parts[embedIdx + 1] && /^[\w-]{11}$/.test(parts[embedIdx + 1])) {
        return parts[embedIdx + 1];
      }
      const shortsIdx = parts.indexOf("shorts");
      if (shortsIdx >= 0 && parts[shortsIdx + 1] && /^[\w-]{11}$/.test(parts[shortsIdx + 1])) {
        return parts[shortsIdx + 1];
      }
    }
  } catch {
    /* not a URL */
  }
  return null;
}

function buildYoutubeEmbedUrl(
  videoId: string,
  startSec?: number,
  endSec?: number,
): string {
  const params = new URLSearchParams();
  if (typeof startSec === "number" && startSec >= 0) params.set("start", String(startSec));
  if (typeof endSec === "number" && endSec > 0) params.set("end", String(endSec));
  const q = params.toString();
  return `https://www.youtube.com/embed/${videoId}${q ? `?${q}` : ""}`;
}

function BlogBlockEditorInner({ initialBlocks, loadError, disabled, onChange }: EditorProps) {
  const editor = useCreateBlockNote({
    initialContent: initialBlocks,
    uploadFile: async (file) => {
      try {
        return await uploadFile(file);
      } catch (err) {
        throw err instanceof ApiError ? new Error(err.message) : new Error("Upload failed");
      }
    },
  });

  const [videoInput, setVideoInput] = useState("");
  const [startSec, setStartSec] = useState("");
  const [endSec, setEndSec] = useState("");
  const [insertError, setInsertError] = useState<string | null>(null);

  function insertVideo() {
    setInsertError(null);
    const id = parseYoutubeVideoId(videoInput);
    if (!id) {
      setInsertError("Enter a YouTube video id or URL");
      return;
    }
    const start = startSec.trim() === "" ? undefined : Number(startSec);
    const end = endSec.trim() === "" ? undefined : Number(endSec);
    if (start != null && (!Number.isFinite(start) || start < 0)) {
      setInsertError("Start seconds must be a non-negative number");
      return;
    }
    if (end != null && (!Number.isFinite(end) || end <= 0)) {
      setInsertError("End seconds must be a positive number");
      return;
    }
    if (start != null && end != null && end <= start) {
      setInsertError("End must be after start");
      return;
    }

    const url = buildYoutubeEmbedUrl(
      id,
      start != null ? Math.floor(start) : undefined,
      end != null ? Math.floor(end) : undefined,
    );

    const videoBlock: PartialBlock = {
      type: "video",
      props: {
        url,
        caption: "",
        showPreview: true,
        previewWidth: 512,
      },
    };

    try {
      const insertAt = editor.getTextCursorPosition().block;
      editor.insertBlocks([videoBlock], insertAt, "after");
      onChange(JSON.stringify(editor.document));
      setVideoInput("");
      setStartSec("");
      setEndSec("");
    } catch (err) {
      // Fallback: append a paragraph marker if video block type is unavailable
      const marker = `[[tih-youtube:${id}${start != null ? `:${Math.floor(start)}` : ""}${
        end != null ? `:${Math.floor(end)}` : ""
      }]]`;
      try {
        const insertAt = editor.getTextCursorPosition().block;
        editor.insertBlocks(
          [{ type: "paragraph", content: marker }],
          insertAt,
          "after",
        );
        onChange(JSON.stringify(editor.document));
        setVideoInput("");
        setStartSec("");
        setEndSec("");
        setInsertError(
          err instanceof Error
            ? `Video block unavailable (${err.message}); inserted marker instead`
            : "Video block unavailable; inserted marker instead",
        );
      } catch (err2) {
        setInsertError(String((err2 as Error).message ?? err2));
      }
    }
  }

  return (
    <div className="admin-blog-block-editor">
      {loadError && (
        <div className="form-error" style={{ marginBottom: 12 }}>
          Could not import previous content: {loadError}
        </div>
      )}
      <div className="admin-blog-insert-video" style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "flex-end",
          }}
        >
          <label style={{ flex: "1 1 220px" }}>
            <span style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Insert video (id or YouTube URL)
            </span>
            <input
              type="text"
              value={videoInput}
              onChange={(e) => setVideoInput(e.target.value)}
              disabled={disabled}
              placeholder="dQw4w9WgXcQ or https://youtu.be/…"
              style={{ width: "100%" }}
            />
          </label>
          <label style={{ flex: "0 0 90px" }}>
            <span style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Start sec</span>
            <input
              type="number"
              min={0}
              step={1}
              value={startSec}
              onChange={(e) => setStartSec(e.target.value)}
              disabled={disabled}
              placeholder="0"
              style={{ width: "100%" }}
            />
          </label>
          <label style={{ flex: "0 0 90px" }}>
            <span style={{ display: "block", fontSize: 12, marginBottom: 4 }}>End sec</span>
            <input
              type="number"
              min={0}
              step={1}
              value={endSec}
              onChange={(e) => setEndSec(e.target.value)}
              disabled={disabled}
              placeholder="—"
              style={{ width: "100%" }}
            />
          </label>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={disabled || !videoInput.trim()}
            onClick={insertVideo}
          >
            Insert video
          </button>
        </div>
        {insertError && (
          <div className="form-error" style={{ marginTop: 8 }}>
            {insertError}
          </div>
        )}
      </div>
      <BlockNoteView
        editor={editor}
        editable={!disabled}
        onChange={() => onChange(JSON.stringify(editor.document))}
      />
    </div>
  );
}

export function BlogBlockEditor({ body, bodyFormat, disabled = false, onChange }: Props) {
  const [initialBlocks, setInitialBlocks] = useState<PartialBlock[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setInitialBlocks(null);

    resolveInitialBlocks(body, bodyFormat)
      .then((blocks) => {
        if (!cancelled) setInitialBlocks(blocks);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(String((err as Error).message ?? err));
          setInitialBlocks(DEFAULT_BLOCKS);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [body, bodyFormat]);

  if (!initialBlocks) {
    return <div className="admin-blog-block-editor-loading">Loading editor…</div>;
  }

  return (
    <BlogBlockEditorInner
      key={`${bodyFormat}:${body}`}
      initialBlocks={initialBlocks}
      loadError={loadError}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

export default BlogBlockEditor;
